import React, { useState, useEffect } from "react";
import SEO from "../seo";
const InstallanApp = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["overview", "steps-to-install-app", "next-steps"];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Install an App | xPage Help Center"
        description="Learn how to quickly and seamlessly install apps on your Xpage store. Follow step-by-step instructions from finding the app to configuring it and getting started."
        keywords="Xpage apps, install app, app installation guide, add apps to store, app setup, app marketplace"
        canonical="https://help.xpage.ai/help/apps/Install%20an%20App"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Install an App 
          </h1>
          <p className="text-sm text-systemLabel">
            Once you've identified the app that fits your store's needs,
            installing it on Xpage is quick and seamless. Follow these steps to
            add and start using the app:
          </p>
        </header>

        {/* Steps to Install an App Section */}
        <section id="steps-to-install-app" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Steps to Install an App
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Log in
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Sign in to your Xpage App Marketplace.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Find the App
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Browse categories or use the search bar to locate the app
                      you want. Click on its details page.
                    </td>
                  </tr>
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Review Details
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Check the app's features, description, and user reviews to
                      ensure it meets your requirements.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Click Install
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      On the app's page, click the Install button.
                    </td>
                  </tr>
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Authorize Access
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      You'll be redirected to your Xpage dashboard. Click
                      Install again to grant the app access to your store data.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Complete Installation
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Once installed, the app will appear in your Apps section
                      within your Xpage dashboard.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Next Steps Section */}
        <section id="next-steps" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Next Steps
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        View Installed Apps
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Go to the Apps page in your dashboard to see all the apps
                      you've added.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Start Using the App
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Click the app icon and follow on-screen instructions to
                      configure it and start leveraging its features to boost
                      your store's performance.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#steps-to-install-app"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "steps-to-install-app"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Steps to Install
              </a>
            </li>
            <li>
              <a
                href="#next-steps"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "next-steps"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Next Steps
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default InstallanApp;
